import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import PropTypes from 'prop-types';

import {
  INVOICE_ACCENT_COLOR,
  currency,
  lineAmount,
  lineFieldError,
  lineFieldHelperText,
  sanitizeDecimalInput,
  sanitizeIntegerInput,
} from '../utils/invoiceEditHelpers';

const HEADER_CELL_SX = { color: 'common.white', fontWeight: 700 };

const InvoiceLineItemsTable = ({ lines, onLineChange, onRemoveClick, onAddRow }) => (
  <>
    <TableContainer>
      <Table size='small' aria-label='invoice line items'>
        <TableHead>
          <TableRow sx={{ bgcolor: INVOICE_ACCENT_COLOR }}>
            <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 260 }}>Product or Service</TableCell>
            <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 90 }}>SKU</TableCell>
            <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 280 }}>Description</TableCell>
            <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 60 }} align='right'>
              Qty
            </TableCell>
            <TableCell sx={{ ...HEADER_CELL_SX, minWidth: 70 }} align='right'>
              Rate
            </TableCell>
            <TableCell sx={HEADER_CELL_SX} align='right'>
              Amount
            </TableCell>
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line, idx) => (
            <TableRow
              key={line.id}
              hover
              sx={{ bgcolor: (idx + 1) % 2 === 0 ? grey[200] : undefined }}
            >
              <TableCell sx={{ minWidth: 260 }}>
                <TextField
                  variant='standard'
                  fullWidth
                  required
                  value={line.drugName}
                  onChange={(e) => onLineChange(idx, 'drugName', e.target.value)}
                  error={lineFieldError(line, 'drugName')}
                  helperText={lineFieldHelperText(line, 'drugName')}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 90 }}>
                <TextField
                  variant='standard'
                  fullWidth
                  required
                  value={line.ndc}
                  onChange={(e) => onLineChange(idx, 'ndc', e.target.value)}
                  error={lineFieldError(line, 'ndc')}
                  helperText={lineFieldHelperText(line, 'ndc')}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 280 }}>
                <TextField
                  variant='standard'
                  fullWidth
                  value={line.description}
                  onChange={(e) => onLineChange(idx, 'description', e.target.value)}
                  error={lineFieldError(line, 'description')}
                  helperText={lineFieldHelperText(line, 'description')}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 60 }} align='right'>
                <TextField
                  variant='standard'
                  type='text'
                  required
                  inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                  value={line.quantity}
                  onChange={(e) =>
                    onLineChange(idx, 'quantity', sanitizeIntegerInput(e.target.value))
                  }
                  error={lineFieldError(line, 'quantity')}
                  helperText={lineFieldHelperText(line, 'quantity')}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 70 }} align='right'>
                <TextField
                  variant='standard'
                  type='text'
                  required
                  inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                  value={line.unitCost}
                  onChange={(e) =>
                    onLineChange(idx, 'unitCost', sanitizeDecimalInput(e.target.value))
                  }
                  error={lineFieldError(line, 'unitCost')}
                  helperText={lineFieldHelperText(line, 'unitCost')}
                />
              </TableCell>
              <TableCell align='right'>{currency(lineAmount(line))}</TableCell>
              <TableCell align='right'>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onRemoveClick(idx)}
                  disabled={lines.length === 1}
                  aria-label='Remove line item'
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    <Grid container justifyContent='flex-end' sx={{ mt: 1, mb: 3 }}>
      <Button size='small' variant='outlined' startIcon={<AddIcon />} onClick={onAddRow}>
        Add Line Item
      </Button>
    </Grid>
  </>
);

InvoiceLineItemsTable.propTypes = {
  lines: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      drugName: PropTypes.string.isRequired,
      ndc: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      quantity: PropTypes.string.isRequired,
      unitCost: PropTypes.string.isRequired,
    })
  ).isRequired,
  onLineChange: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  onAddRow: PropTypes.func.isRequired,
};

export default InvoiceLineItemsTable;
